import React, { useEffect, useState } from 'react';
import Page from '@/model/Page';
import Event from '@/model/Event';
import { useDialog } from '@/hooks/useDialog';
import { useEventClosePageFilter } from '@/context/FinancialPageContext';
import { AxiosError } from 'axios';
import api from '@/services/api';
import { toast } from 'react-toastify';
import useForm from '@/hooks/useForm';
import validators from '@/helpers/validators';
import EventStatus from '@/model/EventStatus';
import { unmask, updateMask } from '@/helpers/masks/generalDate';
import { EventCloseExportReportResponse } from '@/model/EventCloseExportReportResponse';
import { FormInput, FormInputExport, ShouldShowModal, States } from '../../types';
import { EventCloseContainer } from './ui';

export const EventCloseWrapper: React.FC = (): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  // const [page, setPage] = useState<Page<Event, Event>>({
  //   page: 1,
  //   pageSize: 10,
  //   entity: {
  //     eventStatus: EventStatus.ACTIVE,
  //   } as Event,
  //   sort: 'startDate',
  //   order: 'DESC',
  //   total: 1,
  // });
  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(ShouldShowModal.filter);
  const { title, visible, onChangeTitle, onToggle } = useDialog();
  const { page, setPage, clearPage } = useEventClosePageFilter();

  const handleOnPage = async (pageData: Page<Event, Event>): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.post<Page<Event, Event>>('/event/close/financial/page', pageData);
      if (data) {
        setPage(data);
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response?.status !== 401) {
        toast.error(err.message);
      }
    } finally {
      setState(States.default);
    }
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, resetForm } = useForm({
    initialData: {
      historical: 'false',
      filterSearch: '',
      inputSearch: '',
    },
    validators: {
      inputSearch: [validators.required],
    },
  });

  const {
    formData: fromDataExport,
    formErrors: formErrorsExport,
    onChangeFormInput: onChangeFormInputExport,
    isFormValid: isFormValidExport,
    resetForm: resetFormExport,
  } = useForm({
    initialData: {
      startDate: '',
      endDate: '',
    },
    validators: {
      startDate: [validators.required, validators.validDate],
      endDate: [validators.required, validators.validDate],
    },
    formatters: {
      startDate: updateMask,
      endDate: updateMask,
    },
  });

  const handleOnClearFilter = async (): Promise<void> => {
    resetForm();
    const newPage: Page<Event, Event> = {
      page: 1,
      pageSize: 10,
      entity: {
        eventStatus: EventStatus.ACTIVE,
      } as Event,
      sort: 'startDate',
      order: 'DESC',
      total: 1,
    };
    clearPage();
    await handleOnPage(newPage);
  };

  const handleOnFilter = async (): Promise<void> => {
    if (isFormValid()) {
      const newPage: Page<Event, Event> = {
        page: 1,
        pageSize: 10,
        entity: {
          name: formData[FormInput.inputSearch],
          eventStatus:
            formData[FormInput.historical] === 'false' ? EventStatus.ACTIVE : EventStatus.FINISHED,
        } as Event,
        sort: 'startDate',
        order: 'DESC',
        total: 1,
      };
      onToggle();

      await handleOnPage(newPage);
    }
  };

  const handleOnShouldShowModal = ({
    value,
    newTitleModal,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
  }): void => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    onToggle();
  };

  const handleOnClearExport = (): void => {
    resetFormExport();
    onToggle();
  };

  const handleOnExport = async (): Promise<void> => {
    if (isFormValidExport()) {
      try {
        setState(States.loading);
        const startDate = unmask(fromDataExport[FormInputExport.startDate]);
        const endDate = unmask(fromDataExport[FormInputExport.endDate]);
        const payload = {
          startDate,
          endDate,
        };

        onToggle();
        const { data } = await api.post<EventCloseExportReportResponse>(
          '/event-close/export',
          payload,
        );
        toast.success(
          `Estamos processando sua solicitação, em instantes vc receberá o relatório no e-mail: ${data.email}`,
        );
        resetFormExport();
      } catch (error) {
        const err = error as AxiosError;
        if (err.response?.status !== 401) {
          toast.error(err.message);
        }
      } finally {
        setState(States.default);
      }
    }
  };

  const handleOnPaginationChange = async (newPageNumber: number): Promise<void> => {
    const newPage: Page<Event, Event> = {
      ...page,
      page: newPageNumber,
    };
    await handleOnPage(newPage);
  };

  useEffect(() => {
    handleOnPage(page);
  }, []);

  return (
    <EventCloseContainer
      state={state}
      title={title}
      visible={visible}
      onToggle={onToggle}
      page={page}
      onPaginationChange={handleOnPaginationChange}
      onShouldShowModal={handleOnShouldShowModal}
      shouldShowModal={shouldShowModal}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      onClearFilter={handleOnClearFilter}
      onFilter={handleOnFilter}
      fromDataExport={fromDataExport}
      formErrorsExport={formErrorsExport}
      onChangeFormInputExport={onChangeFormInputExport}
      onClearExport={handleOnClearExport}
      onExport={handleOnExport}
    />
  );
};
